import { levelName } from "building-generation/elements/level/levelTools";
import { roomNameFromBuildingRoom } from "building-query/rooms/roomNames";
import type { Building } from "building-types/building/buildingTypes";
import type { CeilingKeyAttributes, CeilingKeyMeasures } from "building-types/dataSequences/builtin/CeilingKAM";
import type { CeilingKAM, CeilingKAMOptions } from "building-types/dataSequences/builtin/CeilingKAM";
import type { BuildingEntityFilter } from "building-types/dataSequences/filterableBuildingEntityTypes";
import type { KeyAttributesAndMeasuresDisplayOptions } from "building-types/dataSequences/measures";
import { buildingEntityPickHandle } from "building-types/entities/buildingEntityPickHandle";
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import { ensureDefined } from "common/utils/assertOfType";
import { keyAttributeNew, keyMeasureNew, keyMeasureOptionsNew } from "doc/utils/dataSequenceTools";
import { faceArea } from "geom/brep";
import type { ConfigurableTable } from "lib/house/dataSequences/dataSequenceConfigurableTable";
import {
  aggregatorColumnNew,
  categoryColumnNew,
  configurableTableNew,
  numberColumnNew,
  SortBy,
  stringColumnNew,
} from "lib/house/dataSequences/dataSequenceConfigurableTable";

interface CeilingKBMData {
  level: string;
  roomId: string;
  roomName: string;
  area: number;
  trayCeilingCount: number;
  handles: DocEntityHandle[];
}

/** @deprecated - use the Building Query API instead */
function getCeilingKBMData({ levels, ceilingsByLevel, ceilings, rooms }: Building): CeilingKBMData[] {
  const ceilingEntries: CeilingKBMData[] = [];

  for (const levelId in levels) {
    const ceilingIdListOnLevel = ceilingsByLevel[levelId];
    if (!ceilingIdListOnLevel) {
      continue;
    }
    for (const ceilingId of ceilingIdListOnLevel) {
      const ceiling = ceilings[ceilingId];
      const ceilingSection = ceiling?.section;

      if (!ceilingSection) {
        continue;
      }

      let ceilingSurfaceArea = 0;
      const { ceilingFaces, roomId: faceRoomId } = ceilingSection;
      const room = rooms[faceRoomId];
      const handles = [buildingEntityPickHandle(room)];

      for (const ceilingFace of ceilingFaces) {
        // Ceiling geometry is double-sided, so only count half of the area
        const mainCeilingSurfaceArea = faceArea(ceilingFace) * 0.5;
        ceilingSurfaceArea += mainCeilingSurfaceArea;
      }

      const subCeilingCount = ceiling.subCeilings.length;

      for (const subCeiling of ceiling.subCeilings) {
        const { outline, ceilingOutlineError, sections } = subCeiling;

        if (ceilingOutlineError || !outline || !sections || sections.length === 0) {
          continue;
        }
        handles.push(subCeiling.pickHandle);

        for (const subCeilingSection of sections) {
          const subFaces = subCeilingSection?.ceilingFaces;
          if (!subFaces) {
            continue;
          }
          for (const subFace of subFaces) {
            // Ceiling geometry is double-sided, so only count half of the area
            const subCeilingSurfaceArea = faceArea(subFace) * 0.5;
            ceilingSurfaceArea += subCeilingSurfaceArea;
          }
        }
      }

      ceilingEntries.push({
        level: ensureDefined(levelName(levels[levelId])),
        roomId: faceRoomId,
        roomName: roomNameFromBuildingRoom(room),
        area: ceilingSurfaceArea,
        trayCeilingCount: subCeilingCount,
        handles,
      });
    }
  }
  return ceilingEntries;
}

export function makeCeilingAreasTable(building: Building): ConfigurableTable {
  const ceilingEntries = getCeilingKBMData(building);
  return configurableTableNew<CeilingKBMData, CeilingKBMData>({
    iterables: ceilingEntries,
    requirement: (x) => {
      if (x.area > 0) {
        return x;
      }
      return false;
    },
    columns: [
      categoryColumnNew("Ceilings"),
      stringColumnNew({
        name: "Level",
        fieldFunc: (x) => x.level,
      }),
      stringColumnNew({
        name: "Room Id",
        fieldFunc: (x) => x.roomId,
        hidden: true,
        useInDiff: true,
      }),
      stringColumnNew({
        name: "Room Name",
        fieldFunc: (x) => x.roomName,
        sortBy: SortBy.Ascending,
      }),
      numberColumnNew({
        name: "Tray Ceiling Count",
        fieldFunc: (x) => x.trayCeilingCount,
      }),
    ],
    aggregator: aggregatorColumnNew({
      name: "Surface Area",
      aggregatorFunc: (x) => (x.area ? x.area : 0),
      stringFormatter: (num) => Math.round(num / 144).toString() + " SF",
    }),
  });
}

export function makeCeilingKAM(ceiling: CeilingKBMData): CeilingKAM {
  return {
    type: "CEILING",
    attributes: {
      level: keyAttributeNew(ceiling.level),
      roomId: keyAttributeNew(ceiling.roomId),
      roomName: keyAttributeNew(ceiling.roomName),
      trayCeilingCount: keyAttributeNew(ceiling.trayCeilingCount.toString()),
      entityName: keyAttributeNew(ceiling.roomId),
    },
    measures: {
      area: keyMeasureNew(ceiling.area, "SQ FT", "SQ IN"),
      trayCeilingCount: keyMeasureNew(ceiling.trayCeilingCount),
      count: keyMeasureNew(1),
    },
    handles: ceiling.handles,
  };
}
export const EMPTY_CEILING_KAM: CeilingKAM = {
  type: "CEILING",
  attributes: {
    level: keyAttributeNew(""),
    roomId: keyAttributeNew(""),
    roomName: keyAttributeNew(""),
    trayCeilingCount: keyAttributeNew(""),
    entityName: keyAttributeNew(""),
  },
  measures: {
    area: keyMeasureNew(0, "SQ FT"),
    trayCeilingCount: keyMeasureNew(0),
    count: keyMeasureNew(0),
  },
  handles: [],
};
export function makeDefaultCeilingKAMOptions(): CeilingKAMOptions {
  return {
    attributes: {},
    measures: {
      area: keyMeasureOptionsNew({ unit: "SQ FT" }),
    },
  };
}
export const defaultCeilingKAMSort = (a: CeilingKAM, b: CeilingKAM) => {
  return a.attributes.roomName.value.localeCompare(b.attributes.roomName.value);
};

/** @deprecated - use the Building Query API instead */
export function buildingCeilingKAMs(building: Building, filter?: BuildingEntityFilter): CeilingKAM[] {
  return getCeilingKBMData(building)
    .filter((ceiling) => {
      if (ceiling.area <= 0) {
        return false;
      }
      if (filter) {
        const generatedCeiling = building.ceilings[ceiling.roomId];
        if (generatedCeiling && !filter(generatedCeiling)) {
          return false;
        }
      }
      return true;
    })
    .map(makeCeilingKAM);
}

export const CEILING_DISPLAY_OPTIONS: KeyAttributesAndMeasuresDisplayOptions<CeilingKeyAttributes, CeilingKeyMeasures> =
  {
    attributes: {
      roomId: {
        hidden: true,
      },
      entityName: {
        hidden: true,
      },
    },
  };

// Attributes used in procure entityHash
export const DEFINES_CEILING: (keyof CeilingKeyAttributes)[] = ["roomId"];
